import { InfoCircleFilled } from "@ant-design/icons";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { Card, Col, Row, Spin, Typography } from "antd";
import { BtnVoltar } from "components/button/BtnVoltar";
import { HelmetWithBreadcrumbs } from "components/HelmetWithBreadcrumbs";
import { useMemo } from "react";
import type { TExportToCSV } from "std/csv";
import { useRelatorioStore } from "../store";
import { CtrlBar } from "./CtrlBar";

const { Title, Paragraph } = Typography;

export function RelatorioContainer<T = unknown, TChild = unknown>({
	loading,
	filterBar,
	table,
	breadCrumbs,
	title,
	refetch,
	csvData,
	registros,
	noFilter,
	extraButtons,
	onImprimir,
}: {
	loading: boolean;
	filterBar: JSX.Element | null;
	table: JSX.Element;
	breadCrumbs: string[];
	title: string;
	refetch: (
		options?: RefetchOptions,
		// biome-ignore lint/suspicious/noExplicitAny: dynamic
	) => Promise<QueryObserverResult<any, Error>>;
	csvData?: TExportToCSV<T, TChild> | undefined;
	registros: T[];
	noFilter?: boolean;
	extraButtons?: JSX.Element;
	onImprimir?: () => void;
}) {
	const isRelatorioRequisitado = useRelatorioStore((s) => s.isRelatorioRequisitado);

	const isEmptyResponse = useMemo(
		() => (!registros || registros?.length === 0) && isRelatorioRequisitado && !loading,
		[registros, loading, isRelatorioRequisitado],
	);

	return (
		<>
			<BtnVoltar />

			<HelmetWithBreadcrumbs title={title} breadcrumbs={breadCrumbs} />

			<Row gutter={[10, 10]}>
				<Col xs={24}>
					<Card>
						{filterBar}

						<Row gutter={[8, 8]} className="mt-3">
							<CtrlBar
								csvData={csvData}
								registros={registros}
								refetch={refetch}
								isEmptyResponse={isEmptyResponse}
								noFilter={noFilter}
								extraButtons={extraButtons}
								onImprimir={onImprimir}
							/>
						</Row>
					</Card>
				</Col>

				<Col xs={24}>
					<Card>
						<Row className="flex justify-center">
							<Spin spinning={loading} size="large" classNames={{ root: "w-full" }}>
								{!isEmptyResponse && isRelatorioRequisitado && !loading && table}

								{isEmptyResponse && (
									<Row className="justify-center w-full">
										<Col className="mr-3">
											<InfoCircleFilled className="mt-4 text-3xl shadow-sm text-sky-500 rounded-s-md" />
										</Col>
										<Col>
											<Title level={5} style={{ margin: "0px" }}>
												Atenção
											</Title>
											<Paragraph className="mb-0">A consulta não retornou nenhum registro.</Paragraph>
											<Paragraph className="mt-0">Tente refazer a consulta com outros parâmetros.</Paragraph>
										</Col>
									</Row>
								)}
							</Spin>
						</Row>
					</Card>
				</Col>
			</Row>
		</>
	);
}
